// /en/glossary — English mirror of /glossary
// SEO: DefinedTermSet + CollectionPage + hreflang parity with /glossary
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, ExternalLink, ChevronRight } from "lucide-react";
import { GLOSSARY, GLOSSARY_BY_CATEGORY } from "@/data/glossary";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { organizationSchema, speakableSchema, BUILD_DATE } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import EducationalTripleKeyLinks from "@/components/EducationalTripleKeyLinks";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";
const URL = `${SITE}/en/glossary`;
const TH_URL = `${SITE}/glossary`;
const TITLE = "Glossary — Translation, Notary, Apostille & Visa Terms | NYC Legal";
const DESC =
  "Bilingual (English/Thai) glossary of 10 key legal terms: Certified Translation, Notary Public. Ask us anything — real experts reply within minutes.";

const CAT_LABELS: Record<string, string> = {
  translation: "Translation",
  notary: "Notary Public",
  legalization: "Legalization / Apostille",
  visa: "Visa",
  marriage: "Marriage",
  document: "Government Documents",
  authority: "Accreditation Bodies",
};

const CATEGORY_ORDER = [
  "translation",
  "notary",
  "legalization",
  "authority",
  "visa",
  "marriage",
  "document",
] as const;

// Map TH service paths to EN equivalents (most use same slug under /en/).
function enHref(thUrl?: string): string | undefined {
  if (!thUrl) return undefined;
  return `/en${thUrl}`;
}

export const Route = createFileRoute("/en/glossary")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/glossary", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/glossary", "en")},
      {
        name: "keywords",
        content:
          "glossary, certified translation thailand, notary public thailand, apostille, MFA legalization, NAATI, police clearance certificate, affidavit single status, power of attorney, non-immigrant o visa, kor ror 2",
      },
      { property: "og:title", content: ctaTitle(TITLE, "/en/glossary", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/glossary", "en")},
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: URL },
      ...hreflangLinks("/glossary", "/en/glossary"),
    ],
    scripts: [
      consolidateGraph([
        organizationSchema(),
        speakableSchema([".aeo-speakable", "h1", "h2", "dt"]),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTermSet",
            "@id": `${URL}#termset`,
            url: URL,
            name: "NYC Legal & Translation Glossary (English)",
            description:
              "Bilingual (English/Thai) glossary of legal, translation, notary, legalization, and visa terminology used in Thailand.",
            inLanguage: "en",
            license: "https://creativecommons.org/licenses/by/4.0/",
            hasDefinedTerm: GLOSSARY.map((t) => ({
              "@type": "DefinedTerm",
              "@id": `${URL}#${t.id}`,
              name: t.termEn,
              alternateName: [t.term, ...(t.alternateNames ?? [])],
              description: t.definitionEn,
              termCode: t.id,
              inDefinedTermSet: `${URL}#termset`,
              url: `${URL}#${t.id}`,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "@id": URL,
            url: URL,
            name: TITLE,
            description: DESC,
            inLanguage: "en",
            dateModified: BUILD_DATE,
            isPartOf: { "@id": `${SITE}#website` },
            about: { "@id": `${SITE}#org` },
            translationOfWork: { "@id": TH_URL },
            hasPart: GLOSSARY.map((t) => ({
              "@type": "DefinedTerm",
              "@id": `${URL}#${t.id}`,
              name: t.termEn,
              alternateName: [t.term, ...(t.alternateNames ?? [])],
              description: t.definitionEn,
              url: `${URL}#${t.id}`,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
              { "@type": "ListItem", position: 2, name: "Glossary", item: URL },
            ],
          }),
        },
      ]),
    ],
  }),
  component: EnGlossaryPage,
});

function EnGlossaryPage() {
  const categories = CATEGORY_ORDER.filter((c) => GLOSSARY_BY_CATEGORY[c]?.length);

  return (
    <main className="min-h-screen bg-navy-deep text-white">
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-14">
        <div className="mx-auto max-w-4xl text-center">
          <div className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-amber-300">
            <BookOpen className="h-3 w-3" /> Glossary
          </div>
          <h1 className="text-3xl font-extrabold leading-tight sm:text-5xl aeo-speakable">
            Legal & Translation<br />
            <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">
              Terminology Reference
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-white/75 aeo-speakable">
            Concise English & Thai definitions with alternate names and links to the related
            service — for use when preparing documents for embassies, Thai authorities, or
            overseas institutions.
          </p>
          <div className="quick-answer mx-auto mt-6 max-w-2xl rounded-2xl border border-amber-400/40 bg-amber-500/10 p-5 text-left" data-speakable="true" itemScope itemType="https://schema.org/Question">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-amber-300">AI Quick Answer</div>
            <h2 className="text-base font-bold text-white sm:text-lg" itemProp="name">What is the difference between Apostille and MFA Legalization?</h2>
            <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
              <p className="direct-answer mt-2 text-sm leading-relaxed text-white/85" itemProp="text">An Apostille is a single MFA certificate accepted in all 125+ Hague Convention member states (Thailand acceded on 30 June 2026 (in force for Thailand 28 February 2027)). MFA Legalization is a broader two-step process: MFA stamps the document, then the destination embassy adds its own seal — required for non-Hague countries like UAE, China, and Vietnam. NYC handles both chains end-to-end.</p>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            {categories.map((c) => (
              <a
                key={c}
                href={`#cat-${c}`}
                className="rounded-full border border-white/20 bg-white/[0.06] px-3 py-1 text-[11px] font-bold text-white/80 hover:border-amber-400/50 hover:text-amber-300"
              >
                {CAT_LABELS[c]}
              </a>
            ))}
          </div>
          <div className="mt-4 text-xs text-white/80">
            <Link to="/glossary" className="hover:text-amber-300">
              ดูภาษาไทย ·  Thai version →
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-4xl space-y-12">
          {categories.map((cat) => (
            <div key={cat} id={`cat-${cat}`} className="scroll-mt-20">
              <h2 className="mb-5 text-lg font-bold text-amber-300">{CAT_LABELS[cat]}</h2>
              <dl className="space-y-4">
                {GLOSSARY_BY_CATEGORY[cat].map((t) => {
                  const enUrl = enHref(t.url);
                  return (
                    <article
                      key={t.id}
                      id={t.id}
                      className="group scroll-mt-24 rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition hover:border-amber-400/30 sm:p-6"
                    >
                      <dt className="flex flex-wrap items-baseline gap-2">
                        <span className="text-lg font-bold text-white aeo-speakable">
                          {t.termEn}
                        </span>
                        <span className="text-sm font-semibold text-amber-200">· {t.term}</span>
                      </dt>
                      <dd className="mt-2 text-sm leading-relaxed text-white/85">
                        {t.definitionEn}
                      </dd>
                      <dd className="mt-2 text-xs leading-relaxed italic text-white/80">
                        TH: {t.definition}
                      </dd>
                      {t.alternateNames && t.alternateNames.length > 0 && (
                        <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px]">
                          <span className="text-white/45">Also known as:</span>
                          {t.alternateNames.map((a) => (
                            <span
                              key={a}
                              className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-white/70"
                            >
                              {a}
                            </span>
                          ))}
                                <RelatedHubGrid locale="en" />
      </div>
                      )}
                      {enUrl && (
                        <div className="mt-4 border-t border-white/10 pt-3">
                          <Link
                            to={enUrl}
                            className="inline-flex items-center gap-1 text-xs font-bold text-amber-300 hover:text-amber-200"
                          >
                            See related service <ChevronRight className="h-3 w-3" />
                          </Link>
                        </div>
                      )}
                    </article>
                  );
                })}
              </dl>
            </div>
          ))}
        </div>

        <div className="mx-auto mt-14 max-w-3xl rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-8 text-center">
          <h2 className="text-xl font-extrabold">Need this for AI / Research?</h2>
          <p className="mt-2 text-sm text-white/75">
            Open data API available (JSON, CC-BY-4.0).
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <a
              href="/api/public/glossary.json"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400"
            >
              <ExternalLink className="h-4 w-4" /> /api/public/glossary.json
            </a>
            <Link
              to="/en/ask"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10"
            >
              AI Quick Answers <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-12 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <EducationalTripleKeyLinks mode="en" />
        </div>
      </section>
    </main>
  );
}
